import {
	createRoutesFromChildren,
	matchRoutes,
	Navigate,
	Outlet,
	Route,
	Routes,
	useLocation,
	useNavigationType,
	useParams,
} from "react-router-dom";

import * as Sentry from "@sentry/react";
import smartlookClient from "smartlook-client";
import PricingPage from "./pages/Pricing/Pricing.tsx";

import { Alert } from "@mui/material";
import Snackbar from "@mui/material/Snackbar";
import { observer } from "mobx-react-lite";
import { useEffect } from "react";
import Joyride from "react-joyride";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./App.scss";
import "./assets/styles/main.scss";
import ErrorAlertWithCode from "./components/common/ErrorAlertWithCode/ErrorAlertWithCode.tsx";
import OnboardingFlowModal from "./components/common/OnboardingFlowModal/OnboardingFlowModal.tsx";
import { ENV_VARS, getCurrentEnv, isCloudEnv, isIntegration, isLocal } from "./config/env";
import RouterConstants from "./constants/RouterConstants.ts";
import BriaDesignEditor from "./DesignEditor/BriaDesignEditor.tsx";
import BriaDesignEditorPopup from "./DesignEditor/BriaDesignEditorPopup.tsx";
import usePostHog from "./hooks/usePostHog.tsx";
import { useAppStore } from "./hooks/useStores.tsx";
import AutomotiveLayout from "./layout/AutomotiveLayout/AutomotiveLayout.tsx";
import HubLayout from "./layout/HubLayout/HubLayout.tsx";
import IframeLayout from "./layout/IframeLayout/IframeLayout.tsx";
import InitIframe from "./layout/IframeLayout/InitIframe.tsx";
import LabsLayout from "./layout/LabsLayout/LabsLayout.tsx";
import MigrationIframeLayout from "./layout/MigrationIframeLayout/MigrationIframeLayout.tsx";
import MobileChecker from "./layout/MobileChecker.tsx";
import PrivateLayout from "./layout/PrivateLayout/PrivateLayout.tsx";
import PublicLayout from "./layout/PublicLayout/PublicLayout.tsx";
import { RedirectConsoleToPlatformFrontend } from "./layout/RedirectConsoleToPlatformFrontend/RedirectConsoleToPlatformFrontend.tsx";
import AttributionReports from "./pages/Account/AttributionReports/AttributionReports.tsx";
import CatalogReport from "./pages/Account/AttributionReports/CatalogReport/CatalogReport.tsx";
import GenerationReport from "./pages/Account/AttributionReports/GenerationReport/GenerationReport.tsx";
import PartnersEarnings from "./pages/Account/AttributionReports/PartnersEarnings/PartnersEarnings.tsx";
import TopImages from "./pages/Account/AttributionReports/TopImages/TopImages.tsx";
import WorkspaceSettings from "./pages/Account/WorkspaceSettings.tsx";
import AdsGeneration from "./pages/AdsGeneration/AdsGeneration.tsx";
import AdsGenerationAPI from "./pages/AdsGeneration/API/AdsGenerationAPI.tsx";
import AdsGenerationBrandsTemplates from "./pages/AdsGeneration/BrandsTemplates/AdsGenerationBrandsTemplates.tsx";
import AdsGenerationDemo from "./pages/AdsGeneration/Demo/AdsGenerationDemo.tsx";
import AdsGenerationIframe from "./pages/AdsGeneration/iFrame/AdsGenerationIframe.tsx";
import AdsGenerationOverview from "./pages/AdsGeneration/Overview/AdsGenerationOverview.tsx";
import AIEditor from "./pages/AIEditor/AIEditor.tsx";
import Automotive from "./pages/Automotive/Automotive.tsx";
import AutomotiveProjects from "./pages/Automotive/Projects/AutomotiveProjects.tsx";
import CreateAutomotiveProject from "./pages/Automotive/Projects/Create/CreateAutomotiveProject.tsx";
import AutomotiveProject from "./pages/Automotive/Projects/Viewer/AutomotiveProject.tsx";
import Fibo from "./pages/Fibo/Fibo.tsx";
import FiboEdit from "./pages/FiboEdit/FiboEdit.tsx";
import FigmaTab from "./pages/Figma/FigmaTab";
import NewIframeConfig from "./pages/IframeNew/Config/NewIframeConfig.tsx";
import iframeStore from "./pages/IframeNew/iframe-store.tsx";
import NewIframeEditor from "./pages/IframeNew/IframeEditor/NewIframeEditor.tsx";
import BackgroundComposition from "./pages/ImageEditing/API/BackgroundComposition.tsx";
import ImageEditingAPI from "./pages/ImageEditing/API/ImageEditingAPI.tsx";
import ObjectContentManipulation from "./pages/ImageEditing/API/ObjectContentManipulation.tsx";
import RestorationEnhancement from "./pages/ImageEditing/API/RestorationEnhancement.tsx";
import StyleAtmosphere from "./pages/ImageEditing/API/StyleAtmosphere.tsx";
import VideoEditingApi from "./pages/ImageEditing/API/VideoEditingApi.tsx";
import ImageEditingDemo from "./pages/ImageEditing/Demo/ImageEditingDemo.tsx";
import ImageEditing from "./pages/ImageEditing/ImageEditing.tsx";
import ImageGenerationAPI from "./pages/ImageGeneration/API/ImageGenerationAPI.tsx";
import ImageGeneration from "./pages/ImageGeneration/ImageGeneration.tsx";
import VideoEditingLab from "./pages/Labs/VideoEditing/VideoEditingLab.tsx";
import LicensedAIPage from "./pages/LicensedAI/LicensedAIPage.tsx";
import MobileNotSupportedPage from "./pages/MobileNotSupportedPage/MobileNotSupportedPage.tsx";
import ModelsAttributionAgent from "./pages/Models/AttributionAgent/ModelsAttributionAgent.tsx";
import ModelsKnowledge from "./pages/Models/Knowledge/ModelsKnowledge.tsx";
import Models from "./pages/Models/Models.tsx";
import ModelsOverview from "./pages/Models/Overview/ModelsOverview.tsx";
import TgAPI from "./pages/NewTG/API/TgAPI.tsx";
import TgImageGenerationConfig from "./pages/NewTG/App/Image/Config/TgImageGenerationConfig.tsx";
import TgApp from "./pages/NewTG/App/TgApp.tsx";
import TgVideoEditingConfig from "./pages/NewTG/App/Video/Config/TgVideoEditingConfig.tsx";
import TgBestPractices from "./pages/NewTG/BestPractices/TgBestPractices.tsx";
import NewTgDatasetViewer from "./pages/NewTG/Datasets/NewViewer/NewTgDatasetViewer.tsx";
import TgAdvancedDatasetViewer from "./pages/NewTG/Datasets/Viewer/TgAdvancedDatasetViewer.tsx";
import TgDatasetViewer from "./pages/NewTG/Datasets/Viewer/TgDatasetViewer.tsx";
import TgImageGenerationDemo from "./pages/NewTG/Demo/Apps/TgImageGenerationDemo.tsx";
import TgDemoTab from "./pages/NewTG/Demo/TgDemoTab.tsx";
import TgIframe from "./pages/NewTG/Iframe/TgIframe.tsx";
import TgAccessMethods from "./pages/NewTG/Integrations/TgAccessMethods.tsx";
import TgEvaluationConfig from "./pages/NewTG/Models/Viewer/Evaluation/Config/TgEvaluationConfig.tsx";
import TgModelEvaluation from "./pages/NewTG/Models/Viewer/Evaluation/TgModelEvaluation.tsx";
import TgModelViewer from "./pages/NewTG/Models/Viewer/TgModelViewer.tsx";
import NewTailoredGeneration from "./pages/NewTG/NewTailoredGeneration.tsx";
import TgOverview from "./pages/NewTG/Overview/TgOverview.tsx";
import TgProjectEditor from "./pages/NewTG/Projects/Editor/TgProjectEditor.tsx";
import TgProjects from "./pages/NewTG/Projects/TgProjects.tsx";
import CreateTgProjectDataset from "./pages/NewTG/Projects/Viewer/Datasets/Create/CreateTgProjectDataset.tsx";
import EditTgProjectDataset from "./pages/NewTG/Projects/Viewer/Datasets/Edit/EditTgProjectDataset.tsx";
import TgProjectDatasetLayout from "./pages/NewTG/Projects/Viewer/Datasets/Layout/TgProjectDatasetLayout.tsx";
import TgProjectDatasets from "./pages/NewTG/Projects/Viewer/Datasets/TgProjectDatasets.tsx";
import CreateTgProjectModel from "./pages/NewTG/Projects/Viewer/Models/Create/CreateTgProjectModel.tsx";
import { FiboCreateTgProjectModel } from "./pages/NewTG/Projects/Viewer/Models/FiboCreate/FiboCreateTgProjectModel.tsx";
import TgProjectModels from "./pages/NewTG/Projects/Viewer/Models/TgProjectModels.tsx";
import TgProjectViewer from "./pages/NewTG/Projects/Viewer/TgProjectViewer.tsx";
import PageNotFound from "./pages/PageNotFound/PageNotFound.tsx";
import PlatformHub from "./pages/PlatformHub/PlatformHub.tsx";
import AutomotiveConfig from "./pages/Playground/Automotive/Config/AutomotiveConfig.tsx";
import ImageToImageConfig from "./pages/Playground/ImageToImage/Config/ImageToImageConfig.tsx";
import Playground from "./pages/Playground/Playground.tsx";
import ProductPlacementConfig from "./pages/Playground/ProductPlacement/Config/ProductPlacementConfig.tsx";
import ProductPlacement from "./pages/Playground/ProductPlacement/ProductPlacement.tsx";
import TextToImageConfig from "./pages/Playground/TextToImage/Config/TextToImageConfig.tsx";
import ProductShotAPI from "./pages/ProductShot/API/ProductShotAPI.tsx";
import ProductShot from "./pages/ProductShot/ProductShot.tsx";
import VideoEditingAPI from "./pages/VideoEditing/API/VideoEditingAPI.tsx";
import VideoEditingOverview from "./pages/VideoEditing/Overview/VideoEditingOverview.tsx";
import VideoEditing from "./pages/VideoEditing/VideoEditing.tsx";
import { isMigrationIframe } from "./utils/migrationIframe.ts";

let PlatformRoutes = Routes;
if (isCloudEnv()) {
	smartlookClient.init("ec245a49a6bf8b418a2b34e773128025ec739027");
	smartlookClient.record({
		forms: true,
		ips: true,
		numbers: true,
		emails: true,
		api: true,
	});

	Sentry.init({
		dsn: "https://32df6e7b9dab6be875c86509240c9a14@o417868.ingest.sentry.io/4505997423345664",
		integrations: [
			new Sentry.BrowserProfilingIntegration(), // for profiling, "Document-Policy" response header should be set to "js-profiling"
			new Sentry.BrowserTracing({
				routingInstrumentation: Sentry.reactRouterV6Instrumentation(
					useEffect,
					useLocation,
					useNavigationType,
					createRoutesFromChildren,
					matchRoutes,
				),
			}),
			new Sentry.Replay({
				maskAllText: false,
				maskAllInputs: false,
				blockAllMedia: false,
				networkDetailAllowUrls: [window.location.origin, ENV_VARS.VITE_PLATFORM_API_URL ?? ""],
			}),
		],
		debug: isIntegration() || isLocal(),
		environment: getCurrentEnv(),

		tracesSampleRate: 0,
		replaysSessionSampleRate: 0,
		replaysOnErrorSampleRate: 1.0,
		ignoreErrors: [/Could not fetch resource from/i],
	});
	PlatformRoutes = Sentry.withSentryReactRouterV6Routing(Routes);
}

function App() {
	const { uiStore, tourStore } = useAppStore();
	const { trackEvent } = usePostHog();
	const location = useLocation();

	// HubSpot's widget script is async and can inject its container well after mount, so a one-off
	// getElementById(...).style toggle can miss it (especially for iframes that never navigate again
	// after landing). A standing stylesheet rule applies the moment the element exists, regardless of timing.
	useEffect(() => {
		if (!iframeStore.isIframe()) return;
		const style = document.createElement("style");
		style.textContent = "#hubspot-messages-iframe-container { display: none !important; }";
		document.head.appendChild(style);
		return () => {
			document.head.removeChild(style);
		};
	}, []);

	useEffect(() => {
		trackEvent("$pageview", {
			$current_url: window.location.href,
			pathname: location.pathname,
			search: location.search,
		});
		const hubspotChatWidget = document.getElementById("hubspot-messages-iframe-container");
		if (hubspotChatWidget) {
			if (
				iframeStore.isIframe() ||
				window.location.pathname.includes(`${RouterConstants.ADS_GENERATION.DEMO.fullPath}/`)
			) {
				hubspotChatWidget.style.setProperty("display", "none", "important");
			} else {
				hubspotChatWidget.style.removeProperty("display");
			}
		}
	}, [location, trackEvent]);

	const RedirectToPlatformHub = () => {
		const { appName } = useParams();

		return (
			<Navigate
				to={appName ? `${RouterConstants.APPS.fullPath}/${appName}` : RouterConstants.APPS.fullPath}
				replace
			/>
		);
	};

	return (
		<>
			<InitIframe />
			<MobileChecker />
			{!iframeStore.isIframe() && !isMigrationIframe() && <RedirectConsoleToPlatformFrontend />}
			<PlatformRoutes>
				{iframeStore.isIframe() ? (
					<Route element={<IframeLayout />}>
						{/* This is needed to show the loader on iframe load */}
						<Route path="/" element={<></>} />
						{iframeStore.iframe?.config.enabled_pages.fibo && (
							<Route path={RouterConstants.LABS.path} element={<LabsLayout />}>
								<Route path={RouterConstants.LABS.FIBO.path} element={<Fibo />} />
								<Route path={RouterConstants.LABS.FIBO_BBQ.path} element={<Fibo />} />
								<Route path={RouterConstants.LABS.FIBO_2.path} element={<Fibo />} />
								<Route path={RouterConstants.LABS.FIBO_LITE.path} element={<Fibo />} />
								<Route path={RouterConstants.LABS.VIDEO_EDITING.path} element={<VideoEditingLab />} />
							</Route>
						)}
						{iframeStore.iframe?.config.enabled_pages.fiboEdit && (
							<Route path={RouterConstants.LABS.path} element={<LabsLayout />}>
								<Route path={RouterConstants.LABS.FIBO_EDIT.fullPath} element={<FiboEdit />} />
							</Route>
						)}
						{iframeStore.iframe?.config.enabled_pages.textToImage && (
							<Route path={RouterConstants.TEXT_TO_IMAGE.fullPath} element={<Playground />}>
								<Route path="" element={<TextToImageConfig />} />
							</Route>
						)}
						{iframeStore.iframe?.config.enabled_pages.textToVector && (
							<Route path={RouterConstants.TEXT_TO_VECTOR.fullPath} element={<Playground />}>
								<Route path="" element={<TextToImageConfig />} />
							</Route>
						)}
						{iframeStore.iframe?.config.enabled_pages.imageToImage && (
							<Route path={RouterConstants.IMAGE_TO_IMAGE.fullPath} element={<Playground />}>
								<Route path="" element={<ImageToImageConfig />} />
							</Route>
						)}
						{iframeStore.iframe?.config.enabled_pages.campaign && (
							<Route
								path={RouterConstants.ADS_GENERATOR.fullPath}
								element={<BriaDesignEditorPopup />}
							></Route>
						)}
						{iframeStore.iframe?.config.enabled_pages.adsEditor && (
							<Route path={RouterConstants.ADS_EDITOR.fullPath} element={<BriaDesignEditor />}>
								<Route path="templates/:type" element={<BriaDesignEditor />} />
							</Route>
						)}

						{iframeStore.iframe?.config.enabled_pages.automotive && (
							<>
								<Route path={RouterConstants.AUTOMOTIVE.path}>
									<Route path="" element={<Automotive />} />
								</Route>
								<Route path={RouterConstants.AUTOMOTIVE_EDITOR.fullPath} element={<Playground />}>
									<Route path="" element={<AutomotiveConfig />} />
								</Route>
								<Route path={RouterConstants.NEW_AUTOMOTIVE_PROJECT.fullPath}>
									<Route path="" element={<CreateAutomotiveProject />} />
								</Route>
								<Route path={RouterConstants.AUTOMOTIVE_PROJECTS.fullPath}>
									<Route path="" element={<AutomotiveProjects />} />
								</Route>
								<Route path={RouterConstants.AUTOMOTIVE_PROJECT_UPLOADED.fullPath}>
									<Route path="" element={<AutomotiveProject />} />
								</Route>
								<Route path={RouterConstants.AUTOMOTIVE_PROJECT_FAVORITES.fullPath}>
									<Route path="" element={<AutomotiveProject />} />
								</Route>
								<Route
									path={RouterConstants.AUTOMOTIVE_PROJECT_SESSION.fullPath}
									element={<Playground />}
								>
									<Route path="" element={<AutomotiveConfig />} />
								</Route>
								<Route
									path={RouterConstants.AUTOMOTIVE_PROJECT_SIMPLE_SESSION.fullPath}
									element={<Playground />}
								>
									<Route path="" element={<AutomotiveConfig />} />
								</Route>
							</>
						)}
						{iframeStore.iframe?.config.enabled_pages.tgTraining && (
							<>
								<Route path={RouterConstants.NEW_TG.PROJECTS.fullPath} element={<TgProjects />} />
								<Route
									path={RouterConstants.NEW_TG.PROJECTS.createPath}
									element={<TgProjectEditor />}
								/>
								<Route path={RouterConstants.NEW_TG.PROJECTS.editPath} element={<TgProjectEditor />} />
								<Route path={RouterConstants.NEW_TG.PROJECTS.viewPath} element={<TgProjectViewer />}>
									<Route index element={<Navigate to={RouterConstants.NEW_TG.MODELS.path} />} />
									<Route
										path={RouterConstants.NEW_TG.MODELS.fullPath}
										element={<TgProjectModels />}
									/>
									<Route
										path={RouterConstants.NEW_TG.DATASETS.fullPath}
										element={<TgProjectDatasets />}
									/>
								</Route>

								{/* Legacy TG routes for iframe as well */}
								<Route
									path={RouterConstants.NEW_TG.LEGACY.PROJECTS.viewPath}
									element={<TgProjectViewer />}
								>
									<Route
										index
										element={<Navigate to={RouterConstants.NEW_TG.LEGACY.MODELS.path} />}
									/>
									<Route
										path={RouterConstants.NEW_TG.LEGACY.MODELS.fullPath}
										element={<TgProjectModels />}
									/>
									<Route
										path={RouterConstants.NEW_TG.LEGACY.DATASETS.fullPath}
										element={<TgProjectDatasets />}
									/>
								</Route>

								<Route element={<Navigate to={RouterConstants.NEW_TG.MODELS.EVALUATION.path} />} />
								<Route
									path={RouterConstants.NEW_TG.MODELS.EVALUATION.fullPath}
									element={<TgModelEvaluation />}
								>
									<Route path="" element={<TgEvaluationConfig />} />
								</Route>
								<Route
									path={RouterConstants.NEW_TG.LEGACY.MODELS.EVALUATION.fullPath}
									element={<TgModelEvaluation />}
								>
									<Route path="" element={<TgEvaluationConfig />} />
								</Route>
								<Route path={RouterConstants.NEW_TG.MODELS.viewPath} element={<TgModelViewer />}>
									<Route
										path={RouterConstants.NEW_TG.MODELS.DATASET.fullPath}
										element={<NewTgDatasetViewer embeddedView />}
									/>
									<Route
										path={`${RouterConstants.NEW_TG.MODELS.DATASET.fullPath}/advanced`}
										element={<NewTgDatasetViewer embeddedView />}
									/>
								</Route>

								{/* Legacy model viewer and dataset routes for iframe */}
								<Route path={RouterConstants.NEW_TG.LEGACY.MODELS.viewPath} element={<TgModelViewer />}>
									<Route
										path={RouterConstants.NEW_TG.LEGACY.MODELS.DATASET.fullPath}
										element={<TgDatasetViewer embeddedView />}
									/>
									<Route
										path={`${RouterConstants.NEW_TG.LEGACY.MODELS.DATASET.fullPath}/advanced`}
										element={<TgAdvancedDatasetViewer embeddedView />}
									/>
								</Route>

								<Route
									path={RouterConstants.NEW_TG.DATASETS.viewPath}
									element={<TgProjectDatasetLayout />}
								>
									<Route index element={<NewTgDatasetViewer />} />
									<Route
										path={RouterConstants.NEW_TG.DATASETS.editPath}
										element={<EditTgProjectDataset />}
									/>
									<Route
										path={RouterConstants.NEW_TG.DATASETS.advancedViewPath}
										element={<NewTgDatasetViewer />}
									/>
								</Route>

								{/* Legacy dataset view/edit/advanced routes for iframe */}
								<Route
									path={RouterConstants.NEW_TG.LEGACY.DATASETS.viewPath}
									element={<TgProjectDatasetLayout />}
								>
									<Route index element={<TgDatasetViewer />} />
									<Route
										path={RouterConstants.NEW_TG.LEGACY.DATASETS.editPath}
										element={<EditTgProjectDataset />}
									/>
									<Route
										path={RouterConstants.NEW_TG.LEGACY.DATASETS.advancedViewPath}
										element={<TgAdvancedDatasetViewer />}
									/>
								</Route>

								<Route
									path={RouterConstants.NEW_TG.MODELS.createFullPath}
									element={<FiboCreateTgProjectModel />}
								/>
								{/* Legacy create model route for iframe */}
								<Route
									path={RouterConstants.NEW_TG.LEGACY.MODELS.createFullPath}
									element={<CreateTgProjectModel />}
								/>
								<Route
									path={RouterConstants.NEW_TG.DATASETS.createFullPath}
									element={<CreateTgProjectDataset />}
								/>
								{/* Legacy create dataset route for iframe */}
								<Route
									path={RouterConstants.NEW_TG.LEGACY.DATASETS.createFullPath}
									element={<CreateTgProjectDataset />}
								/>
								{/* Legacy create project route for iframe */}
								<Route
									path={RouterConstants.NEW_TG.LEGACY.PROJECTS.createPath}
									element={<TgProjectEditor />}
								/>
							</>
						)}
					</Route>
				) : (
					<>
						<Route element={<MigrationIframeLayout />}>
							<Route path={RouterConstants.PRICING.path} element={<PricingPage />} />
							<Route element={<PrivateLayout />}>
								<Route path="/">
									<Route path={RouterConstants.LICENSED_AI.path} element={<LicensedAIPage />} />
									{/*<Route path={RouterConstants.USER_PROFILE.path} element={<UserProfile />} />*/}
									{/* Legacy TG routes*/}
									<Route
										path={RouterConstants.NEW_TG.LEGACY.PROJECTS.viewPath}
										element={<TgProjectViewer />}
									>
										<Route
											index
											element={<Navigate to={RouterConstants.NEW_TG.LEGACY.MODELS.path} />}
										/>
										<Route
											path={RouterConstants.NEW_TG.LEGACY.MODELS.fullPath}
											element={<TgProjectModels />}
										/>
										<Route
											path={RouterConstants.NEW_TG.LEGACY.DATASETS.fullPath}
											element={<TgProjectDatasets />}
										/>
									</Route>
									<Route path={RouterConstants.MODELS.path} element={<Models />}>
										<Route index element={<Navigate to={RouterConstants.MODELS.OVERVIEW.path} />} />
										<Route
											path={RouterConstants.MODELS.OVERVIEW.path}
											element={<ModelsOverview />}
										/>
										<Route
											path={RouterConstants.MODELS.ATTRIBUTION_SERVICE.path}
											element={<ModelsAttributionAgent />}
										/>
										<Route
											path={RouterConstants.MODELS.KNOWLEDGE.path}
											element={<ModelsKnowledge />}
										/>
									</Route>
									{/* Legacy model viewer and dataset routes */}
									<Route
										path={RouterConstants.NEW_TG.LEGACY.MODELS.viewPath}
										element={<TgModelViewer />}
									>
										<Route
											path={RouterConstants.NEW_TG.LEGACY.MODELS.DATASET.fullPath}
											element={<TgDatasetViewer embeddedView />}
										/>
										<Route
											path={`${RouterConstants.NEW_TG.LEGACY.MODELS.DATASET.fullPath}/advanced`}
											element={<TgAdvancedDatasetViewer embeddedView />}
										/>
									</Route>
									<Route path={RouterConstants.IMAGE_GENERATION.path} element={<ImageGeneration />}>
										<Route index element={<ImageGenerationAPI />} />
										<Route
											path={RouterConstants.IMAGE_GENERATION.OVERVIEW.path}
											element={
												<Navigate to={RouterConstants.IMAGE_GENERATION.fullPath} replace />
											}
										/>
										<Route
											path={RouterConstants.IMAGE_GENERATION.API.path}
											element={
												<Navigate to={RouterConstants.IMAGE_GENERATION.fullPath} replace />
											}
										/>
										<Route
											path={RouterConstants.IMAGE_GENERATION.IFRAME.path}
											element={
												<Navigate to={RouterConstants.IMAGE_GENERATION.fullPath} replace />
											}
										/>
										<Route
											path={RouterConstants.IMAGE_GENERATION.COMFYUI.path}
											element={
												<Navigate to={RouterConstants.IMAGE_GENERATION.fullPath} replace />
											}
										/>
										<Route
											path={RouterConstants.IMAGE_GENERATION.DEMO.path}
											element={
												<Navigate to={RouterConstants.IMAGE_GENERATION.fullPath} replace />
											}
										/>
									</Route>
									{/* Legacy dataset view/edit/advanced routes */}
									<Route
										path={RouterConstants.NEW_TG.LEGACY.DATASETS.viewPath}
										element={<TgProjectDatasetLayout />}
									>
										<Route index element={<TgDatasetViewer />} />
										<Route
											path={RouterConstants.NEW_TG.LEGACY.DATASETS.editPath}
											element={<EditTgProjectDataset />}
										/>
										<Route
											path={RouterConstants.NEW_TG.LEGACY.DATASETS.advancedViewPath}
											element={<TgAdvancedDatasetViewer />}
										/>
									</Route>
									<Route
										path={RouterConstants.IMAGE_GENERATION.DEMO.IMAGE_GENERATION.fullPath}
										element={<Playground />}
									>
										<Route path="" element={<TextToImageConfig />} />
									</Route>
									<Route
										path={RouterConstants.IMAGE_GENERATION.DEMO.TEXT_TO_VECTOR.fullPath}
										element={<Playground />}
									>
										<Route path="" element={<TextToImageConfig />} />
									</Route>
									<Route path={RouterConstants.IMAGE_EDITING.path} element={<ImageEditing />}>
										<Route
											index
											element={
												<Navigate
													to={RouterConstants.IMAGE_EDITING.OBJECT_CONTENT_MANIPULATION.path}
													replace
												/>
											}
										/>
										<Route
											path={RouterConstants.IMAGE_EDITING.OBJECT_CONTENT_MANIPULATION.path}
											element={<ObjectContentManipulation />}
										/>
										<Route
											path={RouterConstants.IMAGE_EDITING.STYLE_ATMOSPHERE.path}
											element={<StyleAtmosphere />}
										/>
										<Route
											path={RouterConstants.IMAGE_EDITING.BACKGROUND_COMPOSITION.path}
											element={<BackgroundComposition />}
										/>
										<Route
											path={RouterConstants.IMAGE_EDITING.RESTORATION_ENHANCEMENT.path}
											element={<RestorationEnhancement />}
										/>
										<Route
											path={RouterConstants.IMAGE_EDITING.IMAGE.path}
											element={<ImageEditingAPI />}
										/>
										<Route
											path={RouterConstants.IMAGE_EDITING.VIDEO.path}
											element={<VideoEditingApi />}
										/>
										<Route
											path={RouterConstants.IMAGE_EDITING.OVERVIEW.path}
											element={<Navigate to={RouterConstants.IMAGE_EDITING.fullPath} replace />}
										/>
										<Route
											path={RouterConstants.IMAGE_EDITING.API.path}
											element={<Navigate to={RouterConstants.IMAGE_EDITING.fullPath} replace />}
										/>
										<Route
											path={RouterConstants.IMAGE_EDITING.IFRAME.path}
											element={<Navigate to={RouterConstants.IMAGE_EDITING.fullPath} replace />}
										/>
										<Route
											path={RouterConstants.IMAGE_EDITING.COMFYUI.path}
											element={<Navigate to={RouterConstants.IMAGE_EDITING.fullPath} replace />}
										/>
									</Route>
									<Route
										path={RouterConstants.IMAGE_EDITING.IMAGE.DEMO.fullPath}
										element={<ImageEditingDemo />}
									>
										<Route path="" element={<ImageToImageConfig />} />
									</Route>
									<Route path={RouterConstants.ADS_GENERATION.path} element={<AdsGeneration />}>
										<Route
											index
											element={<Navigate to={RouterConstants.ADS_GENERATION.OVERVIEW.path} />}
										/>
										<Route
											path={RouterConstants.ADS_GENERATION.OVERVIEW.path}
											element={<AdsGenerationOverview />}
										/>
										<Route
											path={RouterConstants.ADS_GENERATION.API.path}
											element={<AdsGenerationAPI />}
										/>
										<Route
											path={RouterConstants.ADS_GENERATION.BRANDS_TEMPLATES.path}
											element={<AdsGenerationBrandsTemplates />}
										/>
										<Route
											path={RouterConstants.ADS_GENERATION.IFRAME.path}
											element={<AdsGenerationIframe />}
										/>
										<Route
											path={RouterConstants.ADS_GENERATION.DEMO.path}
											element={<AdsGenerationDemo />}
										/>
									</Route>

									<Route
										path={RouterConstants.ADS_GENERATION.DEMO.ADS_EDITOR.fullPath}
										element={<BriaDesignEditor />}
									>
										<Route path="templates/:type" element={<BriaDesignEditor />} />
									</Route>

									<Route
										path={RouterConstants.ADS_GENERATION.DEMO.BRANDS_TEMPLATES_EDITOR.fullPath}
										element={<BriaDesignEditor />}
									>
										<Route path="templates/:type" element={<BriaDesignEditor />} />
									</Route>

									<Route
										path={RouterConstants.ADS_GENERATION.DEMO.ADS_GENERATION.fullPath}
										element={<BriaDesignEditorPopup />}
									></Route>

									<Route path={RouterConstants.PRODUCT_SHOT.path} element={<ProductShot />}>
										<Route index element={<ProductShotAPI />} />
										<Route
											path={RouterConstants.PRODUCT_SHOT.OVERVIEW.path}
											element={<Navigate to={RouterConstants.PRODUCT_SHOT.fullPath} replace />}
										/>
										<Route
											path={RouterConstants.PRODUCT_SHOT.API.path}
											element={<Navigate to={RouterConstants.PRODUCT_SHOT.fullPath} replace />}
										/>
										<Route
											path={RouterConstants.PRODUCT_SHOT.COMFY_UI.path}
											element={<Navigate to={RouterConstants.PRODUCT_SHOT.fullPath} replace />}
										/>
										<Route
											path={RouterConstants.PRODUCT_SHOT.DEMO.path}
											element={<Navigate to={RouterConstants.PRODUCT_SHOT.fullPath} replace />}
										/>
									</Route>
									<Route path={RouterConstants.VIDEO_EDITING.path} element={<VideoEditing />}>
										<Route
											index
											element={<Navigate to={RouterConstants.VIDEO_EDITING.OVERVIEW.path} />}
										/>
										<Route
											path={RouterConstants.VIDEO_EDITING.OVERVIEW.path}
											element={<VideoEditingOverview />}
										/>
										<Route
											path={RouterConstants.VIDEO_EDITING.API.path}
											element={<VideoEditingAPI />}
										/>
									</Route>

									{/*<Route*/}
									{/*	path={RouterConstants.CONSUMPTION_AND_BILLING.path}*/}
									{/*	element={<ConsumptionAndBilling />}*/}
									{/*>*/}
									{/*	<Route*/}
									{/*		path={RouterConstants.USAGE_BILLING.path}*/}
									{/*		element={<UsageAndBilling />}*/}
									{/*	/>*/}
									{/*	<Route path={RouterConstants.SUBSCRIPTION.path} element={<Subscription />} />*/}
									{/*</Route>*/}
									<Route
										path={RouterConstants.WORKSPACE_SETTINGS.path}
										element={<WorkspaceSettings />}
									>
										<Route path={RouterConstants.ACCOUNT_IFRAMES.path} element={<Outlet />}>
											<Route index element={<NewIframeConfig />} />
											<Route
												path={RouterConstants.NEW_IFRAMES_EDITOR.path}
												element={<NewIframeEditor />}
											/>
										</Route>

										{/*<Route*/}
										{/*	path={RouterConstants.RELATED_ORGANIZATIONS.path}*/}
										{/*	element={<RelatedOrganizations />}*/}
										{/*/>*/}
										{/*<Route path={RouterConstants.USERS.path} element={<Users />} />*/}
										<Route
											path={RouterConstants.AttributionReports.path}
											element={<AttributionReports />}
										>
											<Route
												path={RouterConstants.GENERATION_REPORT.path}
												element={<GenerationReport />}
											/>
											<Route
												path={RouterConstants.CATALOG_REPORT.path}
												element={<CatalogReport />}
											/>
											<Route path={RouterConstants.TOP_IMAGES.path} element={<TopImages />} />
											<Route
												path={RouterConstants.PARTNERS_EARNINGS.path}
												element={<PartnersEarnings />}
											/>
										</Route>
										{/*<Route path={RouterConstants.AUTH_KEYS.path} element={<ApiKeys />} />*/}
										{/*<Route path={RouterConstants.IP_PROJECTS.path} element={<IpProjects />} />*/}
										{/*<Route path={RouterConstants.MFA_SETTINGS_TAB.path} element={<MfaSettings />} />*/}
									</Route>
									<Route path={RouterConstants.NEW_TG.path} element={<NewTailoredGeneration />}>
										<Route index element={<Navigate to={RouterConstants.NEW_TG.OVERVIEW.path} />} />
										<Route path={RouterConstants.NEW_TG.OVERVIEW.path} element={<TgOverview />} />
										<Route path={RouterConstants.NEW_TG.PROJECTS.path} element={<TgProjects />} />
										<Route
											path={RouterConstants.NEW_TG.LEGACY.PROJECTS.fullPath}
											element={<TgProjects />}
										/>
										<Route path={RouterConstants.NEW_TG.API.path} element={<TgAPI />} />
										<Route path={RouterConstants.NEW_TG.IFRAME.path} element={<TgIframe />} />
										<Route
											path={RouterConstants.NEW_TG.ACCESS_METHODS.path}
											element={<TgAccessMethods />}
										/>
										<Route path={RouterConstants.NEW_TG.DEMO.path} element={<TgDemoTab />} />
										<Route
											path={RouterConstants.NEW_TG.BEST_PRACTICES.path}
											element={<TgBestPractices />}
										/>
										<Route path={RouterConstants.NEW_TG.FIGMA.path} element={<FigmaTab />} />
									</Route>
									<Route
										path={RouterConstants.NEW_TG.DEMO.IMAGE_GENERATION.fullPath}
										element={<TgImageGenerationDemo />}
									>
										<Route path="" element={<TgImageGenerationConfig />} />
									</Route>
									<Route
										path={RouterConstants.NEW_TG.LEGACY.DEMO.IMAGE_GENERATION.fullPath}
										element={<TgImageGenerationDemo />}
									>
										<Route path="" element={<TgImageGenerationConfig />} />
									</Route>
									<Route
										path={RouterConstants.NEW_TG.DEMO.IMAGE_EDITING.fullPath}
										element={<Playground />}
									>
										<Route path="" element={<ImageToImageConfig />} />
									</Route>
									<Route
										path={RouterConstants.NEW_TG.PROJECTS.createPath}
										element={<TgProjectEditor />}
									/>
									{/* Legacy create model route */}
									<Route
										path={RouterConstants.NEW_TG.LEGACY.MODELS.createFullPath}
										element={<CreateTgProjectModel />}
									/>
									<Route
										path={RouterConstants.NEW_TG.PROJECTS.editPath}
										element={<TgProjectEditor />}
									/>
									{/* Legacy create dataset route */}
									<Route
										path={RouterConstants.NEW_TG.LEGACY.DATASETS.createFullPath}
										element={<CreateTgProjectDataset />}
									/>
									{/* Legacy create project route */}
									<Route
										path={RouterConstants.NEW_TG.LEGACY.PROJECTS.createPath}
										element={<TgProjectEditor />}
									/>
									<Route
										path={RouterConstants.NEW_TG.PROJECTS.viewPath}
										element={<TgProjectViewer />}
									>
										<Route index element={<Navigate to={RouterConstants.NEW_TG.MODELS.path} />} />
										<Route
											path={RouterConstants.NEW_TG.MODELS.fullPath}
											element={<TgProjectModels />}
										/>
										<Route
											path={RouterConstants.NEW_TG.DATASETS.fullPath}
											element={<TgProjectDatasets />}
										/>
									</Route>
									<Route element={<Navigate to={RouterConstants.NEW_TG.MODELS.EVALUATION.path} />} />
									<Route
										path={RouterConstants.NEW_TG.MODELS.EVALUATION.fullPath}
										element={<TgModelEvaluation />}
									>
										<Route path="" element={<TgEvaluationConfig />} />
									</Route>
									<Route
										path={RouterConstants.NEW_TG.LEGACY.MODELS.EVALUATION.fullPath}
										element={<TgModelEvaluation />}
									>
										<Route path="" element={<TgEvaluationConfig />} />
									</Route>
									<Route path={RouterConstants.NEW_TG.MODELS.viewPath} element={<TgModelViewer />}>
										<Route
											path={RouterConstants.NEW_TG.MODELS.DATASET.fullPath}
											element={<NewTgDatasetViewer embeddedView />}
										/>
										<Route
											path={`${RouterConstants.NEW_TG.MODELS.DATASET.fullPath}/advanced`}
											element={<NewTgDatasetViewer embeddedView />}
										/>
									</Route>
									<Route
										path={RouterConstants.NEW_TG.DATASETS.viewPath}
										element={<TgProjectDatasetLayout />}
									>
										<Route index element={<NewTgDatasetViewer />} />
										<Route
											path={RouterConstants.NEW_TG.DATASETS.editPath}
											element={<EditTgProjectDataset />}
										/>
										<Route
											path={RouterConstants.NEW_TG.DATASETS.advancedViewPath}
											element={<NewTgDatasetViewer />}
										/>
									</Route>

									<Route
										path={RouterConstants.NEW_TG.MODELS.createFullPath}
										element={<FiboCreateTgProjectModel />}
									/>
									<Route
										path={RouterConstants.NEW_TG.DATASETS.createFullPath}
										element={<CreateTgProjectDataset />}
									/>
								</Route>

								<Route
									path="apps/image-to-image"
									element={
										<Navigate
											to={`/${RouterConstants.APPS.path}/${RouterConstants.IMAGE_TO_IMAGE.path}`}
											replace
										/>
									}
								/>

								<Route
									path="apps/text-to-image"
									element={
										<Navigate
											to={`/${RouterConstants.APPS.path}/${RouterConstants.TEXT_TO_IMAGE.path}`}
											replace
										/>
									}
								/>
								{/* Authenticated user is required */}
							</Route>
						</Route>
						<Route element={<HubLayout />}>
							<Route path={RouterConstants.APPS.path}>
								<Route path="" element={<PlatformHub />} />
								<Route element={<PrivateLayout ignoreOrgs />}>
									<Route path={RouterConstants.PRODUCT_PLACEMENT.path} element={<Playground />}>
										<Route path="" element={<ProductPlacementConfig />} />
									</Route>
									<Route
										path={RouterConstants.PRODUCT_PLACEMENT_GALLERY.path}
										element={<ProductPlacement />}
									/>
								</Route>
								<Route element={<PrivateLayout ignoreOrgs displayAppsWelcomePopup />}>
									<Route path={RouterConstants.TEXT_TO_IMAGE.path} element={<Playground />}>
										<Route path="" element={<TextToImageConfig />} />
									</Route>
									<Route path={RouterConstants.TEXT_TO_VECTOR.path} element={<Playground />}>
										<Route path="" element={<TextToImageConfig />} />
									</Route>
									<Route path={RouterConstants.IMAGE_TO_IMAGE.path} element={<Playground />}>
										<Route path="" element={<ImageToImageConfig />} />
									</Route>
									<Route path={RouterConstants.APPS.TG.path}>
										<Route index element={<Navigate to={RouterConstants.APPS.TG.IMAGE.path} />} />
										<Route path={RouterConstants.APPS.TG.IMAGE.path} element={<TgApp />}>
											<Route path="" element={<TgImageGenerationConfig />} />
										</Route>
										<Route path={RouterConstants.APPS.TG.VIDEO.path} element={<TgApp />}>
											<Route path="" element={<TgVideoEditingConfig />} />
										</Route>
									</Route>
									<Route path={RouterConstants.AI_EDITOR.path} element={<AIEditor />} />
									<Route path={RouterConstants.ADS_EDITOR.fullPath} element={<BriaDesignEditor />}>
										<Route path="templates/:type" element={<BriaDesignEditor />} />
									</Route>
									<Route
										path={RouterConstants.ADS_GENERATOR.fullPath}
										element={<BriaDesignEditorPopup />}
									></Route>
								</Route>
							</Route>
						</Route>
						<Route element={<PublicLayout />}>
							<Route path={RouterConstants.LABS.path} element={<LabsLayout />}>
								<Route path={RouterConstants.LABS.FIBO.path} element={<Fibo />} />
								<Route path={RouterConstants.LABS.FIBO_BBQ.path} element={<Fibo />} />
								<Route path={RouterConstants.LABS.FIBO_2.path} element={<Fibo />} />
								<Route path={RouterConstants.LABS.FIBO_LITE.path} element={<Fibo />} />
								<Route path={RouterConstants.LABS.FIBO_EDIT.path} element={<FiboEdit />} />
								<Route path={RouterConstants.LABS.VIDEO_EDITING.path} element={<VideoEditingLab />} />
							</Route>
						</Route>
						<Route element={<AutomotiveLayout />}>
							<Route element={<PrivateLayout ignoreOrgs />}>
								<Route path={RouterConstants.AUTOMOTIVE.path}>
									<Route path="" element={<Automotive />} />
								</Route>
								<Route path={RouterConstants.AUTOMOTIVE_EDITOR.fullPath} element={<Playground />}>
									<Route path="" element={<AutomotiveConfig />} />
								</Route>
								<Route path={RouterConstants.NEW_AUTOMOTIVE_PROJECT.fullPath}>
									<Route path="" element={<CreateAutomotiveProject />} />
								</Route>
								<Route path={RouterConstants.AUTOMOTIVE_PROJECTS.fullPath}>
									<Route path="" element={<AutomotiveProjects />} />
								</Route>
								<Route path={RouterConstants.AUTOMOTIVE_PROJECT_UPLOADED.fullPath}>
									<Route path="" element={<AutomotiveProject />} />
								</Route>
								<Route path={RouterConstants.AUTOMOTIVE_PROJECT_FAVORITES.fullPath}>
									<Route path="" element={<AutomotiveProject />} />
								</Route>
								<Route
									path={RouterConstants.AUTOMOTIVE_PROJECT_SESSION.fullPath}
									element={<Playground />}
								>
									<Route path="" element={<AutomotiveConfig />} />
								</Route>
							</Route>
						</Route>
						<Route
							path={`${RouterConstants.OLD_APPS.fullPath}/:appName?`}
							element={<RedirectToPlatformHub />}
						/>
						<Route
							path={`${RouterConstants.MOBILE_NOT_SUPPORTED_PAGE.path}`}
							element={<MobileNotSupportedPage />}
						/>
						<Route path={`${RouterConstants.PAGE_NOT_FOUND.path}`} element={<PageNotFound />} />
						<Route
							path="*"
							element={<Navigate to={RouterConstants.PAGE_NOT_FOUND.path} replace={true} />}
						/>
					</>
				)}
			</PlatformRoutes>
			<Joyride
				key={tourStore.refreshKey}
				run={tourStore.run}
				stepIndex={tourStore.stepIndex} // To enable control of the react-joyride
				steps={tourStore.joyrideSteps}
				callback={tourStore.handleJoyrideCallback}
				tooltipComponent={OnboardingFlowModal}
				disableScrolling={false}
				spotlightClicks={tourStore.activeTour?.spotlightClicks}
				disableOverlayClose
				continuous
			/>
			<ToastContainer
				className="toast"
				position="top-center"
				autoClose={3000}
				hideProgressBar
				closeOnClick
				pauseOnHover={false}
				draggable={false}
				icon={false}
			/>
			{uiStore.snackBarProps?.open && (
				<Snackbar
					open={uiStore.snackBarProps?.open}
					autoHideDuration={uiStore.snackBarProps?.autoHideDuration ?? 2000}
					onClose={() => uiStore.hideSnackBarAlert()}
					ContentProps={{ sx: { whiteSpace: "pre-line" } }}
				>
					<Alert
						onClose={() => uiStore.hideSnackBarAlert()}
						severity={uiStore.snackBarProps?.severity}
						sx={{ whiteSpace: "pre-line" }}
					>
						{uiStore.snackBarProps?.message}
					</Alert>
				</Snackbar>
			)}
			<ErrorAlertWithCode
				open={!!uiStore.errorAlertWithCode?.open}
				title={uiStore.errorAlertWithCode?.title}
				code={uiStore.errorAlertWithCode?.code}
				message={uiStore.errorAlertWithCode?.message}
				autoHideDurationMs={uiStore.errorAlertWithCode?.autoHideDuration}
				onClose={() => uiStore.hideErrorAlertWithCode()}
			/>
		</>
	);
}

export default isCloudEnv() ? Sentry.withProfiler(observer(App)) : observer(App);
